import { usePopups } from '@mm-frontend/mm-web-ui-kit';

import StepPhone from '../ui/StepPhone.vue';
import StepCode from '../ui/StepCode.vue';
import StepFillingProfile from '../ui/StepFillingProfile.vue';

import { useSignInCodeStep } from './code-step/useSignInCodeStep';
import { useSignInPhoneStep } from './phone-step/useSignInPhoneStep';
import { useSignInFillingProfileStep } from './profile-filling-step/useSignInFillingProfileStep';

import { SignInController } from './SignInController';
import { SignInPhoneStepController } from './phone-step/SignInPhoneStepController';
import { SignInCodeStepController } from './code-step/SignInCodeStepController';
import { SignInProfileFillingStepController } from './profile-filling-step/SignInProfileFillingStepController';
import { useAuthSDK } from '~/features/auth-sdk';
import CloseFillingStepConfirm from '../ui/CloseFillingStepConfirm.vue';
import { SignInBlockedStepController } from './blocked-step/SignInBlockedStepController';
import StepBlocked from '../ui/StepBlocked.vue';
import { useSignInBlockedStep } from './blocked-step/useSignInBlockedStep';

const { createPopup } = usePopups();
const { sendedOtpData, logout, updateSendedOtpData } = useAuthSDK();

// PhoneStep
const PhoneStepPopup = createPopup({ component: StepPhone });
const phoneStepController = new SignInPhoneStepController({
  popup: PhoneStepPopup,
});
// ---

// CodeStep
const CodeStepPopup = createPopup({ component: StepCode });
const codeStepController = new SignInCodeStepController({
  popup: CodeStepPopup,
});
// ---

// FillingProfileStep
const FillingProfileStepPopup = createPopup({
  component: StepFillingProfile,
});
const closeFillingProfileConfirmPopup = createPopup({ component: CloseFillingStepConfirm });

let confirmCloseFillingProfilePopup: () => void;
let cancelCloseFillingProfilePopup: () => void;

closeFillingProfileConfirmPopup.onClose((isConfirmed: boolean) => {
  return isConfirmed ? confirmCloseFillingProfilePopup?.() : cancelCloseFillingProfilePopup?.();
});

const isCloseBlocked = ref(true);

FillingProfileStepPopup.onBeforeClose(() => {
  return new Promise((resolve, reject) => {
    if (!isCloseBlocked.value) {
      resolve(true);
      return;
    }

    confirmCloseFillingProfilePopup = () => resolve(true);
    cancelCloseFillingProfilePopup = () => reject();

    closeFillingProfileConfirmPopup.show();
  });
});

FillingProfileStepPopup.onClose(() => {
  if (isCloseBlocked.value) {
    logout();
  }
  
  signInController.resetStep();
});

function unblockFillingProfileStepPopup() {
  isCloseBlocked.value = false;
}

const fillingProfileStepController = new SignInProfileFillingStepController({
  popup: FillingProfileStepPopup,
});
// ---

// BlockedStep
const BlockedStepPopup = createPopup({ component: StepBlocked });
const blockedStepController = new SignInBlockedStepController({
  popup: BlockedStepPopup,
});

BlockedStepPopup.onClose(() => {
  resetStep();
  logout();
})
// ---

const signInController = new SignInController({
  phoneStepController,
  codeStepController,
  fillingProfileStepController,
  blockedStepController,
});

function openSignInPopup() {
  updateSendedOtpData();

  if (sendedOtpData.value) {
    signInController.goToCodeStep();
  }

  signInController.openSignInPopup();
}

function closeSignInPopup() {
  signInController.closeSignInPopup();
}

const signInPhoneStep = useSignInPhoneStep(signInController, phoneStepController);
const signInCodeStep = useSignInCodeStep(signInController, codeStepController);
const signInFillingProfileStep = useSignInFillingProfileStep(
  signInController,
  fillingProfileStepController,
  unblockFillingProfileStepPopup,
);
const signInBlockedStep = useSignInBlockedStep(signInController, blockedStepController);

const goToPhoneStep = signInController.goToPhoneStep.bind(signInController);
const goToCodeStep = signInController.goToCodeStep.bind(signInController);
const goToFillingProfileStep = signInController.goToFillingProfileStep.bind(signInController);
const goToBlockedStep = signInController.goToBlockedStep.bind(signInController);

function resetStep() {
  signInController.resetStep();
}

export function useSignIn() {
  return {
    openSignInPopup,
    closeSignInPopup,
    signInPhoneStep,
    signInCodeStep,
    signInFillingProfileStep,
    goToPhoneStep,
    goToCodeStep,
    goToFillingProfileStep,
    unblockFillingProfileStepPopup,
    signInBlockedStep,
    resetStep,
  };
}
